<!--
  @fileoverview PreChatForm Component - Pre-chat data collection form (Vue 3)

  The PreChatForm displays before a chat session begins to collect user information
  such as name, email, phone number, and topic selection. It also handles
  Terms & Conditions and Privacy Policy consent.

  @component PreChatForm
  @displayName Pre-Chat Form

  Features:
  - Configurable fields based on server settings
  - Form validation with Element Plus
  - Topic selection dropdown
  - Terms & Conditions checkbox
  - Privacy Policy checkbox
  - Session initialization on submit

  @example
  <PreChatForm
    :settings="formSettings"
    @setShowPreChatForm="handleShowForm"
    @onPreChatFormSuccess="handleSuccess"
  />
-->
<template>
  <el-form
    ref="prechatFormRef"
    :model="prechatFormFieldDetails"
    :rules="formRules"
    class="p-[15px]"
  >
    <span class="text-field">{{ prechatFormSettings.greeting }}</span>

    <el-divider> </el-divider>

    <el-form-item
      v-if="prechatFormSettings.promptTopic.enabled"
      label="What do you wish to enquire about?"
      prop="chosenTopic"
    >
      <el-select
        v-model="prechatFormFieldDetails.chosenTopic"
        placeholder="Please select your topic"
        filterable
      >
        <el-option
          v-for="(item, index) in prechatFormSettings.promptTopic.topics"
          class="topics-select"
          :key="`topic-option-${index}`"
          :label="capitalize(item)"
          :value="item"
        ></el-option>
      </el-select>
    </el-form-item>

    <el-form-item v-if="prechatFormSettings.promptName" label="Name" prop="name">
      <el-input v-model="prechatFormFieldDetails.name"></el-input>
    </el-form-item>

    <el-form-item v-if="prechatFormSettings.promptEmail" label="Email" prop="email">
      <el-input v-model="prechatFormFieldDetails.email"></el-input>
    </el-form-item>

    <el-form-item v-if="prechatFormSettings.promptNumber" label="Phone Number" prop="number">
      <el-input v-model="prechatFormFieldDetails.number"></el-input>
    </el-form-item>

    <el-form-item v-if="prechatFormSettings.termsAndConditions.enabled" prop="acceptTnC">
      <el-checkbox v-model="prechatFormFieldDetails.acceptTnC">
        I accept the
        <a :href="prechatFormSettings.termsAndConditions.url">Terms & Conditions</a>
      </el-checkbox>
    </el-form-item>

    <el-form-item v-if="prechatFormSettings.privacyPolicy.enabled" prop="acceptPrivacyPolicy">
      <el-checkbox v-model="prechatFormFieldDetails.acceptPrivacyPolicy">
        I accept the
        <a :href="prechatFormSettings.privacyPolicy.url">Privacy Policy</a>
      </el-checkbox>
    </el-form-item>

    <el-form-item>
      <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
      <el-button type="primary" class="w-full" @click="submitPreChatForm">Start Chat</el-button>
    </el-form-item>
  </el-form>
</template>

<script setup>
/**
 * @module components/PreChatForm
 * @description Pre-chat data collection form component (Vue 3 Composition API)
 */
import { ref, reactive, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'
import { ElNotification } from 'element-plus'
import _ from 'lodash-es'

/**
 * Component props
 * @property {Object} settings - Form settings from parent (optional)
 */
defineProps({
  settings: {
    type: Object,
    default: () => ({})
  }
})

/**
 * Component emits
 * @event setShowPreChatForm - Controls form visibility
 * @event onPreChatFormSuccess - Emitted on successful form submission
 */
const emit = defineEmits(['setShowPreChatForm', 'onPreChatFormSuccess'])

/**
 * Pinia store instance
 */
const store = useMainStore()

/**
 * Form element reference for validation
 */
const prechatFormRef = ref(null)

/**
 * Pre-chat form settings from server
 */
const prechatFormSettings = reactive({
  greeting: '',
  promptEmail: false,
  promptName: false,
  promptNumber: false,
  termsAndConditions: {
    enabled: false,
    url: ''
  },
  privacyPolicy: {
    enabled: false,
    url: ''
  },
  promptTopic: {
    enabled: false,
    topics: []
  }
})

/**
 * Form field values
 */
const prechatFormFieldDetails = reactive({
  name: '',
  email: '',
  number: '',
  acceptTnC: false,
  acceptPrivacyPolicy: false,
  chosenTopic: ''
})

/**
 * Validation functions
 */
const validateTopic = (rule, value, callback) => {
  if (!value) {
    callback(new Error('Please select a topic'))
  } else {
    callback()
  }
}

const validateEmail = (rule, value, callback) => {
  const EMAILREGEX =
    /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/
  const isEmail = EMAILREGEX.test(value)
  if (value === '') {
    callback(new Error('Email is required'))
  } else if (!isEmail) {
    callback(new Error('Please enter a valid Email address'))
  } else {
    callback()
  }
}

const validateNumber = (rule, value, callback) => {
  const PHONEREGEX = /^[0-9]+$/
  const isPhoneNumber = PHONEREGEX.test(value)
  if (value === '') {
    callback(new Error('Number is required'))
  } else if (!isPhoneNumber) {
    callback(new Error('Please enter a valid phone number'))
  } else {
    callback()
  }
}

const validateTnC = (rule, value, callback) => {
  if (!value) {
    callback(new Error('Please accept the Terms & Conditions'))
  } else {
    callback()
  }
}

const validatePrivacyPolicy = (rule, value, callback) => {
  if (!value) {
    callback(new Error('Please accept the Privacy Policy'))
  } else {
    callback()
  }
}

/**
 * Form validation rules
 */
const formRules = reactive({
  name: [
    {
      required: true,
      message: 'Name is required',
      trigger: 'blur'
    }
  ],
  email: [
    {
      required: true,
      validator: validateEmail,
      trigger: 'blur'
    }
  ],
  number: [
    {
      required: true,
      validator: validateNumber,
      trigger: 'blur'
    }
  ],
  acceptTnC: [
    {
      required: true,
      validator: validateTnC,
      trigger: 'change'
    }
  ],
  acceptPrivacyPolicy: [
    {
      required: true,
      validator: validatePrivacyPolicy,
      trigger: 'change'
    }
  ],
  chosenTopic: [
    {
      required: true,
      validator: validateTopic,
      trigger: 'blur'
    }
  ]
})

/**
 * Capitalize text using lodash startCase
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
function capitalize(text) {
  return _.startCase(text)
}

/**
 * Fetch pre-chat form settings from server
 */
async function getPreChatFormSettings() {
  try {
    const result = await store.getPrechatformSettings()
    if (_.isEmpty(result)) return

    Object.assign(prechatFormSettings, result)

    const noGreeting = !_.get(prechatFormSettings, 'greeting')
    if (noGreeting) {
      emit('setShowPreChatForm', false)
    }
  } catch (error) {
    emit('setShowPreChatForm', false)
    ElNotification.error({
      title: 'Error',
      position: 'bottom-right',
      message: 'Error getting form settings'
    })
  }
}

/**
 * Validate form using Element Plus form validation
 * @returns {Promise<boolean>} Whether the form is valid
 */
async function validate() {
  if (!prechatFormRef.value) return false

  try {
    await prechatFormRef.value.validate()
    return true
  } catch (error) {
    return false
  }
}

/**
 * Submit pre-chat form
 * Validates form, initializes session, and sends form details to server
 */
async function submitPreChatForm() {
  const isValidForm = await validate()
  if (!isValidForm) return

  const { name, email, number, acceptTnC, acceptPrivacyPolicy, chosenTopic } =
    prechatFormFieldDetails

  const form = {
    name,
    email,
    number,
    acceptTnC,
    acceptPrivacyPolicy,
    chosenTopic
  }

  await store.logout()
  await store.register()
  await store.login()

  try {
    const result = await store.sendPrechatformDetails(form)
    const success = _.get(result, 'success', false)
    if (success) {
      emit('setShowPreChatForm', false)
      emit('onPreChatFormSuccess', {
        isValid: true,
        formData: form
      })
    }
  } catch (e) {
    ElNotification.error({
      title: 'Error',
      position: 'bottom-right',
      message: 'Error sending form details'
    })
  }
}

/**
 * Initialize component - fetch form settings on mount
 */
onMounted(() => {
  getPreChatFormSettings()
})
</script>

<style scoped lang="scss">
.text-field {
  color: #606266;
  font-weight: 500;
  line-height: 40px;
  position: relative;
  font-size: 16px;
}

.topics-select {
  font-family: 'ProximaNova', 'Tahoma', 'Avenir Next', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
}

.text-capitalize {
  text-transform: capitalize;
}

.el-select {
  width: 100%;
}
</style>
